import React, {useState, useEffect, useRef} from 'react'
import { Field } from 'formik'
import * as Yup from 'yup'
import { IEventRisksLevel } from '../../interface'
import {
   UiSelectField,
   UiSwitchField,
   UiAmountField,
   UiNumberField
} from "../../../shared/components/fields/";
import { Reference } from '../../../shared/services/referencesService'
import { RiskLevel } from "../../../shared/models/enums";
import {
   RISK_LEVEL_2_LABEL,
   RISK_LEVEL_3_LABEL,
   SAFETY_LEVEL_LOW_LABEL,
   SAFETY_LEVEL_MEDIUM_LABEL,
   SAFETY_LEVEL_HIGH_LABEL
} from '../../../shared/constants';

const maxEstimatedAmountForLevel0 = 1000000 // 1M
const minEstimatedAmountForLevel2 = 3000000 // 3M
const minEstimatedAmountForLevel3 = 20000000 // 20M

const HIGH_PARTICIPANTS_LABEL = "500 or more";
const MEDIUM_PARTICIPANTS_LABEL = "Between 101 and 500";
const HIGH_SETUP_DESCRIPTION_LABEL = "Complex or hazardous setup";
const MEDIUM_SETUP_DESCRIPTION_LABEL = "Semi-structured setup, outdoor or mixed, possibly with minor build or shared site";
const HIGH_EMERGENCY_SERVICE_LABEL = "More than 60 minutes (remote terrain, restricted coverage, etc.)";
const MEDIUM_EMERGENCY_SERVICE_LABEL = "Between 30 and 60 minutes";
const HIGH_HAZARDOUS_ACTIVITIES_LABEL = "Multiple hazardous activities or equipment requiring advanced planning/mitigation";
const MEDIUM_HAZARDOUS_ACTIVITIES_LABEL = "One or two activities with some risk, but with adequate controls";

const matchesLabel = (list: Reference[] | undefined, selectedValue: any, label: string): boolean => {
   if (!list || selectedValue === undefined || selectedValue === null || selectedValue === '') return false;
   const ref = list.find((item: Reference) => item.itemContent === label);
   return !!ref && String(ref.id) === String(selectedValue);
}

// GetAllNumberOfParticipants() returns {label, value} options (for UiRadioField), not {itemContent, id} references.
const matchesRadioLabel = (list: { label: string, value: string }[] | undefined, selectedValue: any, label: string): boolean => {
   if (!list || selectedValue === undefined || selectedValue === null || selectedValue === '') return false;
   const option = list.find((item) => item.label === label);
   return !!option && String(option.value) === String(selectedValue);
}

export const eventRisksLevelInitialValues: IEventRisksLevel = {
   estimatedTotalValue: 0,
   countryLevelRisk: '',
   riskLevel: '',
   safetyLevel: '',
   riskTags: {
      isStrategic: false,
      isHeritage: false,
      isMulti: false
   },
   numberOfPieces: 0
}

export const eventRisksLevelSchema = Yup.object().shape({
   riskLevel: Yup.string().required(),
   safetyLevel: Yup.string().required(),
   estimatedTotalValue : Yup.number().moreThan(0,"Estimated Total Value must be greater than 0").integer("Estimated Total Value must be an integer"),
   countryLevelRisk: Yup.mixed().notRequired(),
   numberOfPieces: Yup.number().required(),
})

export const eventRisksLevelSchemaAreaField = Yup.object().shape({
   riskLevel: Yup.string().required(),
   safetyLevel: Yup.string().required(),
   countryLevelRisk: Yup.string().required('None is only allowed for risk level 0'),
   estimatedTotalValue : Yup.number().moreThan(0,"Estimated Total Value must be greater than 0").integer("Estimated Total Value must be an integer"),
   numberOfPieces: Yup.number().required('Number of pieces is a required field'),
})

const tooltipHeritageMustBeRiskLevel3 = "Risk level 3 must be chosen if fashion show/heritage pieces are chosen";
const tooltipRiskLevelForcedReasons = "Risk level 2 must be chosen if the estimated total value is between 3M and 19'999'999. Risk level 3 must be chosen if fashion show/heritage pieces are chosen, or if the estimated total value is 20M or more.";
const tooltipSafetyLevelSuggestion = "This value is suggested automatically based on the number of participants, the setup description, the emergency service response time and the hazardous activities. It can be manually overridden if justified; the accountability then remains with the event owner.";

const EventRisksLevel = ({ field, form, ...props }: any) => {
   const [riskLevelList, setRiskLevelList] = useState(props.riskLevel ? props.riskLevel : []);
   const prevEstimatedTotalValueRef = useRef();
   const isSafetyLevelManuallyOverridden = useRef(false);
   useEffect(() => {
      setRiskLevelList(props.riskLevel.slice())
   }, [props.riskLevel]);

   const isSafetyLevelHigh =
      matchesRadioLabel(props.numberOfParticipants, form.values.numberParticipants, HIGH_PARTICIPANTS_LABEL) ||
      matchesLabel(props.setupDescriptionList, form.values.setupDescription, HIGH_SETUP_DESCRIPTION_LABEL) ||
      matchesLabel(props.emergencyServiceList, form.values.emergencyService, HIGH_EMERGENCY_SERVICE_LABEL) ||
      matchesLabel(props.hazardousActivitiesList, form.values.hazardousActivities, HIGH_HAZARDOUS_ACTIVITIES_LABEL) ||
      !!form.values.isPublic;

   const mediumSafetyCriteriaCount = [
      matchesRadioLabel(props.numberOfParticipants, form.values.numberParticipants, MEDIUM_PARTICIPANTS_LABEL),
      matchesLabel(props.setupDescriptionList, form.values.setupDescription, MEDIUM_SETUP_DESCRIPTION_LABEL),
      matchesLabel(props.emergencyServiceList, form.values.emergencyService, MEDIUM_EMERGENCY_SERVICE_LABEL),
      matchesLabel(props.hazardousActivitiesList, form.values.hazardousActivities, MEDIUM_HAZARDOUS_ACTIVITIES_LABEL),
      !!form.values.isSemiPublic
   ].filter(Boolean).length;

   const isSafetyLevelMedium = !isSafetyLevelHigh && mediumSafetyCriteriaCount >= 2;

   useEffect(() => {
      if (isSafetyLevelManuallyOverridden.current) return;
      const label = isSafetyLevelHigh ? SAFETY_LEVEL_HIGH_LABEL : (isSafetyLevelMedium ? SAFETY_LEVEL_MEDIUM_LABEL : SAFETY_LEVEL_LOW_LABEL);
      const safetyRef = (props.safetyLevel || []).find((ref: Reference) => ref.itemContent === label);
      if (safetyRef) {
         form.setFieldValue("safetyLevel", safetyRef.id);
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isSafetyLevelHigh, isSafetyLevelMedium, props.safetyLevel]);

   const isRiskLevel3Forced = form.values.riskTags.isHeritage || form.values.eventType === 5 || form.values.estimatedTotalValue >= minEstimatedAmountForLevel3;
   const isRiskLevel2Forced = !isRiskLevel3Forced && form.values.estimatedTotalValue >= minEstimatedAmountForLevel2;

   useEffect(() => {
      if (isRiskLevel3Forced) {
          const riskLevel3 = props.riskLevel.find((ref: Reference) => ref.itemContent === RISK_LEVEL_3_LABEL);
          if (riskLevel3) {
              form.setFieldValue("riskLevel", riskLevel3.id);
          }
      } else if (isRiskLevel2Forced) {
          const riskLevel2 = props.riskLevel.find((ref: Reference) => ref.itemContent === RISK_LEVEL_2_LABEL);
          if (riskLevel2) {
              form.setFieldValue("riskLevel", riskLevel2.id);
          }
      } else {
          form.setFieldValue("riskLevel", "");
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isRiskLevel3Forced, isRiskLevel2Forced, props.riskLevel]);

   useEffect(() => {
       if (form.values.eventType === 5) {
           // Fashion show selected
           form.setFieldValue("riskLevel", 4);
       }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [form.values.eventType]);

   useEffect(() => {
      if(prevEstimatedTotalValueRef.current !== form.values.estimatedTotalValue) {
         prevEstimatedTotalValueRef.current = form.values.estimatedTotalValue;
         if(form.values.estimatedTotalValue >= maxEstimatedAmountForLevel0 && props.riskLevel.find((r: Reference) => r.id === RiskLevel.zero)) {
            setRiskLevelList(props.riskLevel.slice(1, props.riskLevel.length));
            if(form.values.riskLevel === RiskLevel.zero) {
               form.setFieldValue('riskLevel', '');
            }
         }
         else {
            setRiskLevelList(props.riskLevelBackup.slice());
         }
      }
   }, [form, props.riskLevel, props.riskLevelBackup]);

   useEffect(() => {
      if (!form.values.numberOfPieces) {
         form.setFieldValue("numberOfPieces", 0)
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [form.values.numberOfPieces]);

   return (
       <div>
           <div>
               <label className="double-trailing">
                   (**) This field is mandatory if risk level is greater than 0 (zero)
               </label>
               <Field
                   id="fieldEstimatedTotalValue"
                   name="estimatedTotalValue"
                   label="Estimated Total Value (*)"
                   component={UiAmountField}
                   type="number"
                   min={0}
               />
               <Field
                   id="numberOfPieces"
                   name="numberOfPieces"
                   label="Number of pieces (*)"
                   component={UiNumberField}
                   min={0}
               />
               <Field
                   name="countryLevelRisk"
                   label="Area Risk Level (**)"
                   component={UiSelectField}
                   enableValue_None={true}
                   options={props.countryLevelRisk ? props.countryLevelRisk : []}
               />
               <Field
                   name="riskTags.isStrategic"
                   column="quarter"
                   // check forreplace with new tooltip text.
                   tooltip={tooltipHeritageMustBeRiskLevel3}
                   label="Open displays"
                   component={UiSwitchField}
               />
               <Field
                   name="riskTags.isHeritage"
                   column="quarter"
                   tooltip={tooltipHeritageMustBeRiskLevel3}
                   label="Heritage pieces"
                   component={UiSwitchField}
               />
               <Field
                   name="riskLevel"
                   column="quarter"
                   label="Security Risk Level (*)"
                   tooltip={tooltipRiskLevelForcedReasons}
                   component={UiSelectField}
                   options={riskLevelList}
                   disabled={isRiskLevel3Forced || isRiskLevel2Forced}
               />
               <p
                   className="message-prevent"
                   hidden={!isRiskLevel3Forced && !isRiskLevel2Forced}
               >
                   {tooltipRiskLevelForcedReasons}
               </p>
               <Field
                   name="safetyLevel"
                   column="quarter"
                   label="Event Safety Risk Level"
                   tooltip={tooltipSafetyLevelSuggestion}
                   component={UiSelectField}
                   options={props.safetyLevel ? props.safetyLevel : []}
                   onChange={() => { isSafetyLevelManuallyOverridden.current = true; }}
               />
           </div>
       </div>
   );
}
export default EventRisksLevel;